type Catalog = Record<string, string>;

/** Every catalogue in ./locales is a language (a new language = one new file); `_meta.name` / `_meta.dir` describe it. */
const files = import.meta.glob<Catalog>('./locales/*.json', { eager: true, import: 'default' });
const catalogs: Record<string, Catalog> = Object.fromEntries(
  Object.entries(files).map(([path, cat]) => [/([\w-]+)\.json$/u.exec(path)![1]!, cat]),
);

export type Language = { code: string; name: string; dir: 'ltr' | 'rtl' };
export const LANGUAGES: readonly Language[] = Object.entries(catalogs)
  .map(([code, c]) => ({ code, name: c['_meta.name'] ?? code, dir: c['_meta.dir'] === 'rtl' ? ('rtl' as const) : ('ltr' as const) }))
  .sort((a, b) => a.code.localeCompare(b.code));

export const catalogue = (code: string): Catalog => catalogs[code] ?? catalogs.en!;
export const dirOf = (code: string): 'ltr' | 'rtl' => LANGUAGES.find((l) => l.code === code)?.dir ?? 'ltr';

/** A saved choice first, then the browser language, then English. */
export const pickLanguage = (saved: string | null, browser: string): string => {
  const has = (c: string | null | undefined) => Boolean(c && catalogs[c]);
  if (has(saved)) return saved!;
  const b = browser.split('-')[0];
  return has(b) ? b! : 'en';
};

/** Panel text comes only from catalogues; unknown language falls back to English, unknown key to the key. */
export const makeT = (locale: string) => {
  const base = locale.split('-')[0] ?? 'en';
  const cat = catalogs[base] ?? catalogs.en!;
  return (key: string, vars: Record<string, string | number> = {}): string =>
    (cat[key] ?? catalogs.en![key] ?? key).replace(/\{(\w+)\}/gu, (_, k: string) => String(vars[k] ?? ''));
};
export type T = ReturnType<typeof makeT>;
